import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom'
import { addLocale, locale } from 'primereact/api'
import { AuthProvider } from '@/contexts/AuthContextSupabase'
import { LanguageProvider } from '@/contexts/LanguageContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AdminRoute } from '@/components/AdminRoute'
import { MainLayout } from '@/components/layout/MainLayout'
import { ProgressSpinner } from 'primereact/progressspinner'
import styles from './App.module.css'

// Configure Japanese locale for PrimeReact Calendar
addLocale('ja', {
  firstDayOfWeek: 0,
  dayNames: [
    '日曜日',
    '月曜日',
    '火曜日',
    '水曜日',
    '木曜日',
    '金曜日',
    '土曜日',
  ],
  dayNamesShort: ['日', '月', '火', '水', '木', '金', '土'],
  dayNamesMin: ['日', '月', '火', '水', '木', '金', '土'],
  monthNames: [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ],
  monthNamesShort: [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ],
  today: '今日',
  clear: 'クリア',
})
locale('ja')

// Lazy load all pages for code splitting
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
)
const HomePage = lazy(() =>
  import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
)
const OrderPage = lazy(() =>
  import('@/pages/Order').then((m) => ({ default: m.OrderPage }))
)
const OrderNewPage = lazy(() =>
  import('@/pages/OrderNewPage').then((m) => ({ default: m.OrderNewPage }))
)
const AreaMasterPage = lazy(() =>
  import('@/pages/AreaMasterPage').then((m) => ({ default: m.AreaMasterPage }))
)
const InquirySearchPage = lazy(() =>
  import('@/pages/InquirySearchPage').then((m) => ({
    default: m.InquirySearchPage,
  }))
)
const SalesReportPage = lazy(() =>
  import('@/pages/SalesReportPage').then((m) => ({
    default: m.SalesReportPage,
  }))
)
const StaffMasterPage = lazy(() =>
  import('@/pages/StaffMasterPage').then((m) => ({
    default: m.StaffMasterPage,
  }))
)
const CategoryMasterPage = lazy(() =>
  import('@/pages/CategoryMasterPage').then((m) => ({
    default: m.CategoryMasterPage,
  }))
)
const NoticeRegisterPage = lazy(() =>
  import('@/pages/NoticeRegisterPage').then((m) => ({
    default: m.NoticeRegisterPage,
  }))
)

// Loading fallback component
function LoadingFallback() {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingContent}>
        <ProgressSpinner
          style={{ width: '60px', height: '60px' }}
          strokeWidth="4"
        />
        <p className={styles.loadingText}>読み込み中...</p>
      </div>
    </div>
  )
}

// Root layout with providers
function RootLayout() {
  return (
    <div className="jp-theme">
      <AuthProvider>
        <LanguageProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Outlet />
          </Suspense>
        </LanguageProvider>
      </AuthProvider>
    </div>
  )
}

// Protected layout wrapper
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  )
}

// Create the data router with route configuration
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/',
        element: <ProtectedLayout />,
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          {
            path: 'orders',
            element: <OrderPage />,
          },
          {
            path: 'order-new',
            element: <OrderNewPage />,
          },
          {
            path: 'order-new/:orderId',
            element: <OrderNewPage />,
          },
          {
            path: 'inquiries',
            element: <InquirySearchPage />,
          },
          {
            path: 'reports-sales',
            element: <SalesReportPage />,
          },
          {
            path: 'notice-register',
            element: <NoticeRegisterPage />,
          },
          {
            path: 'staffs',
            element: (
              <AdminRoute>
                <StaffMasterPage />
              </AdminRoute>
            ),
          },
          {
            path: 'categories',
            element: (
              <AdminRoute>
                <CategoryMasterPage />
              </AdminRoute>
            ),
          },
          {
            path: 'areas',
            element: (
              <AdminRoute>
                <AreaMasterPage />
              </AdminRoute>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])

function App() {
  return <RouterProvider router={router} />
}

export default App
